import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import {
  getFeaturedCategories,
  type CategorySlug,
  type CuratedCategory,
} from '../../assets/categoryImages';
import { useSelectedCountry } from '../../hooks/useSelectedCountry';
import { CategoryIcon } from './CategoryIcon';

/**
 * One category tile. Renders a photo cover when the curated category has
 * one (European launch taxonomy); otherwise falls back to a branded icon
 * tile — the chosen look for markets whose categories ship without photo
 * assets (e.g. Senegal trades & home services).
 */
function CategoryTile({
  cat,
  onClick,
}: {
  cat: CuratedCategory;
  onClick: (slug: CategorySlug) => void;
}) {
  const { t } = useTranslation();
  const label = t(cat.i18nKey);

  return (
    <ButtonBase
      onClick={() => onClick(cat.slug)}
      aria-label={label}
      sx={{
        width: '100%',
        borderRadius: 3,
        display: 'block',
        textAlign: 'left',
        overflow: 'hidden',
        position: 'relative',
        aspectRatio: '4 / 5',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 8px 24px rgba(0,0,0,0.45)'
            : '0 8px 24px rgba(26,24,22,0.10)',
        transition: 'transform 280ms ease, box-shadow 280ms ease',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 18px 40px rgba(0,0,0,0.6)'
              : '0 18px 40px rgba(26,24,22,0.18)',
        },
        '&:hover .cat-image': { transform: 'scale(1.08)' },
        '&:hover .cat-icon': { transform: 'scale(1.12)' },
        '&:hover .cat-arrow': { opacity: 1, transform: 'translateX(0)' },
      }}
    >
      {cat.cover ? (
        <Box
          className="cat-image"
          component="img"
          src={cat.cover}
          alt={label}
          loading="lazy"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 600ms ease',
          }}
        />
      ) : (
        // Icon tile — branded gradient + large category icon. Used when
        // a category has no photo asset (Senegal trade/home services).
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? `linear-gradient(150deg, ${theme.palette.primary.dark} 0%, #1f1b17 100%)`
                : `linear-gradient(150deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }}
        >
          <CategoryIcon
            code={cat.slug}
            className="cat-icon"
            sx={{
              fontSize: { xs: '3.25rem', sm: '4rem' },
              color: 'common.white',
              opacity: 0.95,
              transition: 'transform 400ms ease',
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.78) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          p: { xs: 1.75, sm: 2.25 },
          color: 'common.white',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'rgba(255,255,255,0.85)',
              letterSpacing: '0.14em',
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {t('home.discoverCta')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.125rem', sm: '1.35rem' },
              lineHeight: 1.2,
              textShadow: '0 2px 8px rgba(0,0,0,0.4)',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box
          className="cat-arrow"
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: 'common.white',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            opacity: { xs: 1, sm: 0 },
            transform: { xs: 'translateX(0)', sm: 'translateX(-8px)' },
            transition: 'all 280ms ease',
          }}
        >
          <ArrowForwardRoundedIcon sx={{ fontSize: '1.25rem' }} />
        </Box>
      </Box>
    </ButtonBase>
  );
}

export function CategoryShowcase() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Featured categories follow the visitor's selected market — Senegal
  // shows its own taxonomy, every other market keeps the global set.
  const { countryCode } = useSelectedCountry();
  const featured = getFeaturedCategories(countryCode);

  const handleClick = (slug: CategorySlug) => {
    navigate(`/category/${slug}`);
  };

  // When categories carry section groups (Senegal A/B/C structure) render
  // them under labelled headings, preserving definition order. Markets
  // without groups (Europe) render a single flat grid exactly as before.
  const hasGroups = featured.some((c) => c.group);

  if (!hasGroups) {
    return (
      <Grid container spacing={2.5}>
        {featured.map((cat) => (
          <Grid item xs={6} sm={4} md={3} key={cat.slug}>
            <CategoryTile cat={cat} onClick={handleClick} />
          </Grid>
        ))}
      </Grid>
    );
  }

  // Group consecutively so each section keeps its definition order.
  const sections: { group: string; items: CuratedCategory[] }[] = [];
  for (const cat of featured) {
    const group = cat.group ?? '';
    const last = sections[sections.length - 1];
    if (last && last.group === group) last.items.push(cat);
    else sections.push({ group, items: [cat] });
  }

  return (
    <Stack spacing={{ xs: 4, md: 5 }}>
      {sections.map((section) => (
        <Box key={section.group}>
          {section.group && (
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              {t(section.group)}
            </Typography>
          )}
          <Grid container spacing={2.5}>
            {section.items.map((cat) => (
              <Grid item xs={6} sm={4} md={3} key={cat.slug}>
                <CategoryTile cat={cat} onClick={handleClick} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Stack>
  );
}
